import { useState } from "react";
import useSWR from "swr";

import { api, type Me } from "../services/api";
import { stopImpersonation } from "../services/impersonation";
import { Icon } from "./Icon";

/**
 * Read-only "view as user" bar.
 *
 * When an iQuest staff member is impersonating a client user, /api/me returns
 * `impersonating: true` (the rest of the payload reflects the impersonated
 * user). This loud, fixed strip sits at the very top of every page so the staff
 * member can never forget the session is active or that it is read-only, and
 * offers a one-click Exit. Renders nothing for normal sessions.
 */
export function ImpersonationBanner() {
  const { data: me } = useSWR<Me>("/api/me", () => api.me());
  const [exiting, setExiting] = useState(false);

  if (!me?.impersonating) return null;

  const company = me.company_name ? ` · ${me.company_name}` : "";

  async function handleExit() {
    setExiting(true);
    try {
      // Navigates away (hard reload), so no need to reset `exiting` on success.
      await stopImpersonation();
    } catch {
      setExiting(false);
    }
  }

  return (
    <div className="impersonation-bar" role="alert">
      <span className="impersonation-bar-icon" aria-hidden="true">
        <Icon name="eye" size={18} />
      </span>
      <span className="impersonation-bar-text">
        Viewing as <strong>{me.display_name}</strong>
        {company}.{" "}
        <span className="impersonation-bar-note">Read-only preview.</span>
      </span>
      <button
        type="button"
        className="impersonation-bar-exit"
        onClick={handleExit}
        disabled={exiting}
      >
        {exiting ? "Exiting…" : "Exit"}
      </button>
    </div>
  );
}
